document.fonts.ready.then(function () {
  gsap.registerPlugin(SplitText, ScrollTrigger);

  /**
   * Animates titles using SplitText.
   * @param {string} selector - The CSS selector for the titles.
   * @param {object} animConfig - Animation configuration object (delay, start, etc.).
   * @param {object} splitConfig - SplitText configuration object (e.g., { wordsClass: '...' }).
   */
  function animateSplitText(selector, animConfig, splitConfig = {}) {
    const { start, ...mainAnimConfig } = animConfig;

    const targets = gsap.utils.toArray(selector);
    targets.forEach((target) => {
      gsap.set(target, { opacity: 1, visibility: "visible" });

      const split = new SplitText(target, { type: "words", ...splitConfig });
      gsap.fromTo(
        split.words,
        { y: 10, opacity: 0, filter: "blur(8px)" },
        {
          y: 0,
          opacity: 1,
          filter: "blur(0px)",
          stagger: 0.1,
          duration: 0.5,
          ease: "power1.out",
          scrollTrigger: {
            trigger: target,
            start: start || "top 80%",
            toggleActions: "play none none none",
          },

          ...mainAnimConfig,
        }
      );
    });
  }

  /**
   * Animates elements in batches with a fade and slide up effect.
   * @param {string} selector - The CSS selector for the elements to animate.
   * @param {string} startValue - The start position for the ScrollTrigger.
   */
  function animateBatchFadeUp(selector, startValue = "top 85%") {
    const targets = gsap.utils.toArray(selector);
    if (targets.length > 0) {
      ScrollTrigger.batch(targets, {
        start: startValue,
        once: true,
        onEnter: (batch) => {
          gsap.fromTo(
            batch,
            { autoAlpha: 0, y: 40 },
            {
              autoAlpha: 1,
              y: 0,
              duration: 1,
              stagger: 0.15,
              ease: "power2.out",
            }
          );
        },
      });
    }
  }

  /**
   * Animates elements on a timeline with a stagger effect.
   * @param {string} elementSelector - Selector for the elements to animate.
   * @param {string} triggerSelector - Selector for the container that triggers the animation.
   * @param {object} fromVars - Initial properties for the animation (e.g., { x: 50 }).
   */
  function animateStaggeredTimeline(
    elementSelector,
    triggerSelector,
    fromVars,
    startValue = "top 70%"
  ) {
    const targets = gsap.utils.toArray(elementSelector);
    if (targets.length === 0) return;

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerSelector,
        start: startValue,
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      targets,
      { autoAlpha: 0, ...fromVars },
      {
        autoAlpha: 1,
        x: 0,
        y: 0,
        duration: 1.2,
        stagger: 0.2,
        ease: "power2.out",
      },
      "-=0.7"
    );
  }

  /**
   * Animates the hero section's device mockups.
   * Creates a staggered entrance where the side phones slide in horizontally,
   * and all three devices fade and slide up into their final positions.
   */
  function animateHeroDevices() {
    const container = document.querySelector(".hero-devices");
    if (!container) return;

    const phones = gsap.utils.toArray(
      ".hero-devices .elementor-widget-pp-devices"
    );
    if (phones.length === 0) return;

    ScrollTrigger.matchMedia({
      "(min-width: 769px)": function () {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        });

        phones.forEach((phone, index) => {
          let xFinal = 0;
          if (index === 0) xFinal = 190;
          else if (index === 2) xFinal = -190;

          tl.fromTo(
            phone,
            { autoAlpha: 0, y: 100 },
            {
              autoAlpha: 1,
              y: 0,
              x: xFinal,
              duration: 1.4,
              ease: "power2.out",
            },
            index * 0.2
          );
        });
      },

      "(max-width: 768px)": function () {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: "top 95%",
            toggleActions: "play none none none",
          },
        });

        phones.forEach((phone, index) => {
          let xFinal = "0%";
          if (index === 0) xFinal = "20%";
          else if (index === 2) xFinal = "-20%";

          tl.fromTo(
            phone,
            { autoAlpha: 0, y: 100 },
            {
              autoAlpha: 1,
              y: 0,
              x: xFinal,
              duration: 1.4,
              ease: "power2.out",
            },
            index * 0.2
          );
        });
      },
    });
  }

  /**
   * Creates a sequenced timeline animation for the features section.
   * The phone mockup slides in from the left, immediately followed by
   * a staggered entrance of the feature cards from the right.
   */
  function animatePhoneAndFeatures() {
    const trigger = ".section-features";
    if (!document.querySelector(trigger)) return;

    let tl = gsap.timeline({
      scrollTrigger: {
        trigger: trigger,
        start: "top 70%",
        toggleActions: "play none none none",
      },
    });

    // NOTIFICATION: Hide phone mockup animation for now
    // tl.fromTo(
    //   ".phone-mockup",
    //   { x: -100, autoAlpha: 0 },
    //   {
    //     x: 0,
    //     autoAlpha: 1,
    //     duration: 1.2,
    //     ease: "power2.out",
    //     overwrite: "auto",
    //   }
    // );
    tl.fromTo(
      ".feature-card",
      { x: 100, autoAlpha: 0 },
      {
        x: 0,
        autoAlpha: 1,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2,
        overwrite: "auto",
      },
      "-=0.7"
    );
  }

  /**
   * Initializes and orchestrates all the GSAP animations for the site.
   * This function acts as the main entry point and should be called
   * once the document and fonts are ready.
   */
  function initAnimations() {
    const responsiveAnimations = [
      {
        type: "batch",
        selector: ".gsap-fade-up .elementor-heading-title",
        desktopStart: "top 80%",
        mobileStart: "top 95%",
      },
      {
        type: "batch",
        selector: ".gsap-fade-up-object",
        desktopStart: "top 80%",
        mobileStart: "top 95%",
      },

      {
        type: "stagger",
        selector: ".active-feature-card",
        trigger: ".section-active-features",
        desktopVars: { from: { x: 50 } },
        mobileVars: { from: { x: 50 }, start: "bottom bottom+=350px" },
      },
      {
        type: "stagger",
        selector: ".next-features-card",
        trigger: ".section-next-features",
        desktopVars: { from: { x: 200 } },
        mobileVars: { from: { x: 50 }, start: "top 80%" },
      },
      {
        type: "stagger",
        selector: ".options-card",
        trigger: ".section-options",
        desktopVars: { from: { y: 50 } },
        mobileVars: { from: { y: 100 }, start: "top 80%" },
      },
    ];

    const setupAnimations = (isDesktop) => {
      responsiveAnimations.forEach((config) => {
        if (config.type === "batch") {
          const startValue = isDesktop
            ? config.desktopStart
            : config.mobileStart;
          animateBatchFadeUp(config.selector, startValue);
        } else if (config.type === "stagger") {
          const vars = isDesktop ? config.desktopVars : config.mobileVars;
          animateStaggeredTimeline(
            config.selector,
            config.trigger,
            vars.from,
            vars.start
          );
        }
      });
    };

    ScrollTrigger.matchMedia({
      "(min-width: 769px)": () => setupAnimations(true),
      "(max-width: 768px)": () => setupAnimations(false),
    });

    animateHeroDevices();
    animatePhoneAndFeatures();
    animateSplitText(".gsap-fade-blur .elementor-heading-title", {
      delay: 0.3,
    });
    animateSplitText(
      ".gsap-gradient-reveal .elementor-heading-title",
      {},
      { wordsClass: "gradient-word" }
    );
    animateSplitText(".gsap-fade2-blur .elementor-heading-title", {
      delay: 0.8,
      start: "top 100%",
    });
  }

  initAnimations();
});
